import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Send, Sparkles, Globe, ExternalLink, Plus, History, X, MessageSquare, Trash2, Volume2, VolumeX, Loader2, ChevronDown, Paperclip } from "lucide-react";
import { MicButton } from "@/components/MicButton";
import { toast } from "sonner";
import { refreshFlights } from "@/components/dashboard/FlightsCard";
import { JarvisText } from "@/components/JarvisText";
import { playQuizWord, primeQuizAudio } from "@/lib/quizAudio";

const JARVIS_IMG = "https://static.prod-images.emergentagent.com/jobs/47440676-455b-415d-a656-32c4bba91c19/images/e7c5bee9ca26687529352ae6a149086b13e9babdbdee8f65009e93b70ca1b38d.png";

const INITIAL_GREETING = { role: "jarvis", text: "Hej! Jag är Edit. Fråga mig om dagen, kommande prov eller vad familjen behöver göra. Jag använder Claude och kan hjälpa dig att planera, förklara och öva. Jag följer vårt samtal och söker på webben när det behövs." };

const VOICES = [
  { id: "nova", label: "Nova", desc: "Varm & vänlig (kvinnlig)" },
  { id: "alloy", label: "Alloy", desc: "Neutral, energisk" },
  { id: "shimmer", label: "Shimmer", desc: "Mjuk & klar (kvinnlig)" },
  { id: "onyx", label: "Onyx", desc: "Djup & lugn (manlig)" },
  { id: "echo", label: "Echo", desc: "Tydlig & saklig (manlig)" },
  { id: "fable", label: "Fable", desc: "Brittisk accent, berättande" },
];

const formatRelativeTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "nyss";
  if (min < 60) return `${min} min sedan`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} h sedan`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days} dag${days === 1 ? "" : "ar"} sedan`;
  return d.toLocaleDateString("sv-SE", { month: "short", day: "numeric" });
};

export default function JarvisPage() {
  const [messages, setMessages] = useState([INITIAL_GREETING]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [useWeb, setUseWeb] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // Röst: när Edit svarar — auto-spela om frågan kom från Mic
  const [autoSpeakReplies, setAutoSpeakReplies] = useState(
    () => localStorage.getItem("jarvis_auto_speak") === "1"
  );
  // Vilken meddelandeindex spelar för tillfället (för stop-knapp)
  const [playingIdx, setPlayingIdx] = useState(null);
  const [loadingTtsIdx, setLoadingTtsIdx] = useState(null);
  // Vald röst — sparas i localStorage
  const [voice, setVoice] = useState(
    () => localStorage.getItem("jarvis_voice") || "nova"
  );
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const audioRef = useRef(null);
  const lastFromMicRef = useRef(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    localStorage.setItem("jarvis_voice", voice);
  }, [voice]);

  useEffect(() => {
    localStorage.setItem("jarvis_auto_speak", autoSpeakReplies ? "1" : "0");
  }, [autoSpeakReplies]);

  // Auto-spela senaste Edit-svaret om frågan kom från Mic + auto-speak på
  useEffect(() => {
    if (!autoSpeakReplies || !lastFromMicRef.current) return;
    const last = messages[messages.length - 1];
    if (last?.role !== "jarvis" || !last.text) return;
    lastFromMicRef.current = false;
    speakMessage(messages.length - 1, last.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setPlayingIdx(null);
  };

  const speakMessage = async (idx, text) => {
    // Toggle: klick på den som spelar = stoppa
    if (playingIdx === idx) {
      stopAudio();
      return;
    }
    stopAudio();
    setLoadingTtsIdx(idx);
    try {
      const { data } = await api.post(
        "/jarvis/voice/speak",
        { text, voice },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => {
        setPlayingIdx(null);
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setPlayingIdx(null);
        URL.revokeObjectURL(url);
        toast.error("Kunde inte spela upp");
      };
      setPlayingIdx(idx);
      await audio.play();
    } catch (e) {
      toast.error("TTS-fel");
    } finally {
      setLoadingTtsIdx(null);
    }
  };

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const { data } = await api.get("/jarvis/sessions?limit=10");
      setSessions(data.sessions || []);
    } catch (e) {
      console.warn("loadSessions fel", e);
    } finally {
      setSessionsLoading(false);
    }
  };

  useEffect(() => {
    if (historyOpen) loadSessions();
  }, [historyOpen]);

  const startNewChat = () => {
    setMessages([INITIAL_GREETING]);
    setSessionId(null);
    setInput("");
    setHistoryOpen(false);
    toast.success("Ny chatt startad");
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/jarvis/sessions/${encodeURIComponent(sid)}`);
      const rows = data.messages || [];
      const msgs = [INITIAL_GREETING];
      rows.forEach((r) => {
        if (r.message) msgs.push({ role: "user", text: r.message });
        if (r.reply) msgs.push({
          role: "jarvis", text: r.reply,
          sources: r.sources || [], web_used: r.web_used || false,
        });
      });
      setMessages(msgs);
      setSessionId(sid);
      setHistoryOpen(false);
    } catch {
      toast.error("Kunde inte ladda samtalet");
    }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Radera detta samtal permanent?")) return;
    try {
      await api.delete(`/jarvis/sessions/${encodeURIComponent(sid)}`);
      // Om vi tar bort den aktiva sessionen → starta om
      if (sid === sessionId) {
        setMessages([INITIAL_GREETING]);
        setSessionId(null);
      }
      await loadSessions();
      toast.success("Samtal raderat");
    } catch {
      toast.error("Kunde inte radera");
    }
  };

  const send = async (overrideText = null) => {
    const baseText = overrideText !== null ? overrideText : input;
    if (!baseText.trim()) return;
    primeQuizAudio();
    const msg = baseText.trim();
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    if (overrideText === null) setInput("");
    setLoading(true);
    const doPost = () => api.post("/jarvis/chat", {
      message: msg,
      session_id: sessionId,
      use_web: useWeb,
    }, { timeout: 180000 });
    try {
      let data;
      try {
        ({ data } = await doPost());
      } catch (first) {
        // Auto-retry endast vid nätverksfel/omstart — aldrig timeout (undvik dubbla ändringar)
        const st = first?.response?.status;
        if (first?.code === "ECONNABORTED" || (st && ![502, 503, 504].includes(st))) throw first;
        await new Promise((r) => setTimeout(r, 3000));
        ({ data } = await doPost());
      }
      setSessionId(data.session_id);
      if (data.tts_word) playQuizWord(data.tts_word);
      setMessages((prev) => [...prev, {
        role: "jarvis",
        text: data.reply,
        ttsWord: data.tts_word || null,
        sources: data.sources || [],
        web_used: data.web_used || false,
      }]);
      // Om Edit lade till/tog bort/delade en flight-bevakning → uppdatera dashboard direkt
      if (data.tool === "watch_flight" || data.tool === "unwatch_flight"
          || data.tool === "share_flight_watch") {
        refreshFlights();
      }
    } catch (e) {
      setMessages((prev) => [...prev, { role: "jarvis", text: "Jag kunde inte svara just nu." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";  // tillåt samma fil igen
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Max 25 MB per fil");
      return;
    }
    const note = input.trim();
    setInput("");
    const sizeKb = Math.round(file.size / 1024);
    setMessages((prev) => [...prev, {
      role: "user",
      text: `📎 ${file.name} (${sizeKb} kB)${note ? `\n${note}` : ""}`,
    }]);
    setUploading(true);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (sessionId) fd.append("session_id", sessionId);
      if (note) fd.append("note", note);
      const { data } = await api.post("/jarvis/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setSessionId(data.session_id);
      setMessages((prev) => [...prev, {
        role: "jarvis",
        text: data.reply,
        sources: data.sources || [],
        web_used: data.web_used || false,
      }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "okänt fel";
      setMessages((prev) => [...prev, { role: "jarvis", text: `Kunde inte läsa filen: ${detail}` }]);
    } finally {
      setUploading(false);
      setLoading(false);
    }
  };

  const handleTranscript = (text, payload) => {
    const routed = payload?.routed;
    lastFromMicRef.current = true;

    // Visa alltid vad användaren sa
    setMessages((prev) => [...prev, { role: "user", text }]);

    if (!routed) {
      // Fallback: ingen routing → vanlig chat
      send(text);
      return;
    }

    const kind = routed.kind;
    const confirmation = routed.confirmation || "";

    // För question: visa Edit svar direkt i chatten
    if (kind === "question") {
      const ar = routed.action_result || {};
      if (ar.session_id) setSessionId(ar.session_id);
      setMessages((prev) => [...prev, {
        role: "jarvis",
        text: confirmation,
        sources: ar.sources || [],
        web_used: ar.web_used || false,
      }]);
      return;
    }

    // För övriga: visa kategorisk bekräftelse i chatten + toast
    const emoji = kind === "todo" ? "✅" : kind === "memory" ? "🏠" :
                  kind === "code" ? "🔑" : kind === "briefing" ? "📧" : "❓";
    setMessages((prev) => [...prev, {
      role: "jarvis",
      text: `${emoji} ${confirmation}`,
      kind_badge: kind,
    }]);
    if (routed.ok) {
      toast.success(`${emoji} ${kind === "todo" ? "Påminnelse skapad" : kind === "memory" ? "Sparat i minne" : kind === "code" ? "Kod sparad" : kind === "briefing" ? "Briefing köad" : "Klart"}`);
    } else {
      toast.error(confirmation);
    }
  };

  return (
    <div className="px-5 pt-3 flex flex-col h-screen pb-32" data-testid="jarvis-page">
      <div className="flex items-center gap-3 mb-4">
        <img src={JARVIS_IMG} alt="Edit" className="w-12 h-12 rounded-full" />
        <div className="flex-1 min-w-0">
          <p className="label-eyebrow flex items-center gap-1"><Sparkles size={11} /> AI-Assistent · Dual-Brain</p>
          <h1 className="font-display text-2xl font-medium mt-0.5">Edit</h1>
        </div>
        <button
          onClick={startNewChat}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs border border-[color:var(--border-subtle)] bg-white text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)] transition-colors"
          data-testid="jarvis-new-chat"
          title="Ny chatt"
        >
          <Plus size={12} />
          Ny
        </button>
        <button
          onClick={() => setHistoryOpen(true)}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs border border-[color:var(--border-subtle)] bg-white text-[color:var(--text-secondary)] hover:bg-[color:var(--bg-elevated)] transition-colors"
          data-testid="jarvis-history"
          title="Tidigare samtal"
        >
          <History size={12} />
        </button>
        <button
          onClick={() => setUseWeb(!useWeb)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
            useWeb
              ? "bg-[color:var(--moss)] text-white border-[color:var(--moss)]"
              : "bg-white text-[color:var(--text-tertiary)] border-[color:var(--border-subtle)]"
          }`}
          data-testid="jarvis-web-toggle"
          title={useWeb ? "Claude kan söka på webben" : "Snabbläge (endast Claude)"}
        >
          <Globe size={12} />
        </button>
        <button
          onClick={() => setAutoSpeakReplies(!autoSpeakReplies)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs border transition-colors ${
            autoSpeakReplies
              ? "bg-[color:var(--jarvis)] text-white border-[color:var(--jarvis)]"
              : "bg-white text-[color:var(--text-tertiary)] border-[color:var(--border-subtle)]"
          }`}
          data-testid="jarvis-autospeak-toggle"
          title={autoSpeakReplies ? "Auto-uppläsning på (efter Mic-fråga)" : "Auto-uppläsning av"}
        >
          <Volume2 size={12} />
        </button>
        <div className="relative">
          <button
            onClick={() => setVoicePickerOpen((v) => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border-2 border-[color:var(--jarvis)]/30 bg-white text-[color:var(--jarvis)] font-medium hover:bg-[color:var(--jarvis)]/5"
            data-testid="jarvis-voice-picker"
            title="Byt röst"
          >
            <Volume2 size={11} />
            <span>{VOICES.find((v) => v.id === voice)?.label || "Röst"}</span>
            <ChevronDown size={10} />
          </button>
          {voicePickerOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setVoicePickerOpen(false)}
              />
              <div
                className="absolute right-0 top-9 z-50 w-56 bg-white rounded-xl border border-[color:var(--border-subtle)] shadow-lg p-1.5"
                data-testid="jarvis-voice-menu"
              >
                {VOICES.map((v) => (
                  <button
                    key={v.id}
                    onClick={() => {
                      setVoice(v.id);
                      setVoicePickerOpen(false);
                      // Test-spela en kort fras med ny röst
                      speakMessage(-1, `Hej, jag är ${v.label}.`);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs hover:bg-[color:var(--bg-elevated)] flex items-start gap-2 ${
                      voice === v.id ? "bg-[color:var(--bg-elevated)]" : ""
                    }`}
                    data-testid={`jarvis-voice-${v.id}`}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-[color:var(--text-primary)]">{v.label}</p>
                      <p className="text-[10px] text-[color:var(--text-tertiary)]">{v.desc}</p>
                    </div>
                    {voice === v.id && (
                      <Volume2 size={11} className="text-[color:var(--jarvis)] mt-0.5" />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-3 pr-1">
        {messages.map((m, i) => (
          <div
            key={`jmsg-${i}-${m.role}-${(m.text || '').slice(0,24)}`}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`message-${i}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${
                m.role === "user"
                  ? "bg-[color:var(--moss)] text-white"
                  : "bg-white border border-[color:var(--border-subtle)]"
              }`}
            >
              {m.role === "jarvis" ? <JarvisText text={m.text} /> : m.text}
              {m.role === "jarvis" && (
                <div className="mt-1.5 flex items-center justify-end gap-3">
                  {m.ttsWord && (
                    <button
                      onClick={() => playQuizWord(m.ttsWord)}
                      className="inline-flex items-center gap-1 text-[10px] font-medium text-[color:var(--moss)] hover:opacity-75"
                      data-testid={`quiz-word-tts-${i}`}
                    >
                      <Volume2 size={11} /> Uttala &quot;{m.ttsWord}&quot;
                    </button>
                  )}
                  <button
                    onClick={() => speakMessage(i, m.text)}
                    disabled={loadingTtsIdx === i}
                    className="inline-flex items-center gap-1 text-[10px] text-[color:var(--text-tertiary)] hover:text-[color:var(--moss)] disabled:opacity-50"
                    title={playingIdx === i ? "Stoppa uppläsning" : "Läs upp"}
                    data-testid={`jarvis-tts-${i}`}
                  >
                    {loadingTtsIdx === i ? (
                      <Loader2 size={11} className="animate-spin" />
                    ) : playingIdx === i ? (
                      <VolumeX size={11} />
                    ) : (
                      <Volume2 size={11} />
                    )}
                    {playingIdx === i ? "Stoppa" : "Läs upp"}
                  </button>
                </div>
              )}
              {m.role === "jarvis" && m.web_used && (
                <div className="mt-1 flex items-center gap-1 text-[10px] text-[color:var(--text-tertiary)]">
                  <Globe size={9} /> Webbsökning aktiv
                </div>
              )}
              {m.role === "jarvis" && Array.isArray(m.sources) && m.sources.length > 0 && (
                <div className="mt-2 pt-2 border-t border-[color:var(--border-subtle)] space-y-1">
                  <p className="text-[10px] uppercase tracking-wider text-[color:var(--text-tertiary)]">Källor</p>
                  {m.sources.map((s, j) => (
                    <a
                      key={s.url || `jsrc-${i}-${j}`}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 text-xs text-[color:var(--moss)] hover:underline"
                      data-testid={`jarvis-source-${i}-${j}`}
                    >
                      <span className="opacity-60">[{j + 1}]</span>
                      <span className="truncate flex-1">{s.title}</span>
                      <ExternalLink size={10} className="flex-shrink-0" />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-[color:var(--border-subtle)] rounded-2xl px-4 py-2.5 text-sm text-[color:var(--text-tertiary)] flex items-center gap-2">
              {useWeb ? (
                <>
                  <Globe size={12} className="animate-pulse" />
                  Edit tänker och söker vid behov…
                </>
              ) : (
                "Edit skriver…"
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className="fixed left-1/2 -translate-x-1/2 w-full max-w-md md:max-w-lg px-5"
        style={{ bottom: "calc(5rem + env(safe-area-inset-bottom))" }}
      >
        <div className="flex gap-2 bg-white p-2 rounded-2xl border border-[color:var(--border-subtle)]">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            data-testid="jarvis-file-input"
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || uploading}
            size="icon"
            variant="ghost"
            className="rounded-xl flex-shrink-0 text-[color:var(--text-secondary)] hover:text-[color:var(--moss)]"
            title="Ladda upp fil (PDF, bild, ljud, doc, txt)"
            data-testid="jarvis-upload-button"
          >
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
          </Button>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !loading && send()}
            placeholder={useWeb ? "Fråga Edit (med webbsök)..." : "Fråga Edit (snabbläge)..."}
            className="border-0 focus-visible:ring-0"
            data-testid="jarvis-input"
            disabled={loading}
          />
          <MicButton onTranscript={handleTranscript} disabled={loading} />
          <Button
            onClick={send}
            disabled={loading || !input.trim()}
            size="icon"
            className="rounded-xl bg-[color:var(--moss)] hover:bg-[#1f421f] flex-shrink-0"
            data-testid="jarvis-send-button"
          >
            <Send size={16} />
          </Button>
        </div>
      </div>

      {historyOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center"
          onClick={() => setHistoryOpen(false)}
          data-testid="jarvis-history-backdrop"
        >
          <div
            className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-[color:var(--border-subtle)]">
              <div className="flex items-center gap-2">
                <History size={18} className="text-[color:var(--moss)]" />
                <h3 className="font-semibold">Tidigare samtal</h3>
              </div>
              <button
                onClick={() => setHistoryOpen(false)}
                className="p-1.5 rounded-lg hover:bg-[color:var(--bg-elevated)]"
                data-testid="jarvis-history-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {sessionsLoading ? (
                <p className="text-center text-sm text-[color:var(--text-tertiary)] py-8">Laddar…</p>
              ) : sessions.length === 0 ? (
                <p className="text-center text-sm text-[color:var(--text-tertiary)] py-8">
                  Inga tidigare samtal än.
                </p>
              ) : (
                <div className="space-y-1">
                  {sessions.map((s) => (
                    <button
                      key={s.session_id}
                      onClick={() => openSession(s.session_id)}
                      className={`w-full text-left p-3 rounded-lg flex items-start gap-3 hover:bg-[color:var(--bg-elevated)] transition-colors group ${
                        s.session_id === sessionId ? "bg-[color:var(--bg-elevated)]" : ""
                      }`}
                      data-testid={`jarvis-session-${s.session_id}`}
                    >
                      <MessageSquare size={16} className="text-[color:var(--text-tertiary)] flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium line-clamp-2 leading-snug">{s.title}</p>
                        <p className="text-[10px] text-[color:var(--text-tertiary)] mt-1">
                          {s.turns} meddelande{s.turns === 1 ? "" : "n"} · {formatRelativeTime(s.last_at)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => deleteSession(s.session_id, e)}
                        className="p-1.5 rounded opacity-0 group-hover:opacity-100 hover:bg-[color:var(--danger-bg,#FEE)] transition-opacity flex-shrink-0"
                        title="Radera"
                        data-testid={`jarvis-session-delete-${s.session_id}`}
                      >
                        <Trash2 size={13} className="text-[color:var(--danger)]" />
                      </button>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
